import React from "react";
import { createRoot } from "react-dom/client";
import LoginApp from "./LoginApp";
import "./../assets/css/login-new.css";
import { applyAuthNewTheme } from "../lib/applyAuthNewTheme";

applyAuthNewTheme();
import { fillPageMetaDetails } from "../pagemetadetails";
import { devLog } from '../commons/LogUtils';
import { isProductionEnv } from "../commons/VersionUtil";
import "../engagebay-iframe";
import "../testimonials";
import engagebayBaseAPI from "../api";
import OurDomainScriptModule from "../OurDomainScriptCode";
import { captureExtensionContext } from "../commons/util/activateFlow";

// ── Module-level side effects ─────────────────────────────────────────────────
// GA and GTM are already injected by login.html; GrowSumo and browser check are login-specific.

blockUnsupportedBrowser();
loadGrowSumoScript();
captureExtensionContext();

// ── Bootstrap ─────────────────────────────────────────────────────────────────

/**
 * Fetches login dependencies from the server then mounts LoginApp into the page root.
 * Page meta (title, favicon, description) is set here before React renders so there
 * is no flash of the default EngageBay title on reseller-branded pages.
 */
async function bootstrap() {

    let deps: { [key: string]: any } = {};

    try {
        const res = await engagebayBaseAPI.get(
            "/rest/api/login/get-login-dependencies" + window.location.search
        );
        devLog("loginDeps", res.data);
        deps = res.data || {};

        const prefs = deps.prefs;
        if (isProductionEnv() && prefs) {
            OurDomainScriptModule.injectOurDomainScriptCode(false, prefs.isClient, prefs.resellerID, null);
        }
        if (prefs?.isClient) {
            fillPageMetaDetails(prefs);
        }
    } catch (err) {
        devLog("Failed to load login dependencies", err);
        // Render with empty deps so the page still shows (error from server
        // may appear via the deps.error field on retry, but we must mount).
    }

    // Set page meta from prefs (fallback to EngageBay defaults)
    const prefs = deps.prefs || {};
    const titleEl = document.getElementById('page-title');
    if (titleEl) titleEl.textContent = (prefs.pageTitle || 'EngageBay CRM') + ' - Login';
    const descEl = document.getElementById('page-description');
    if (descEl) descEl.setAttribute('content', prefs.pageDescription || 'Default page description');
    const favEl = document.getElementById('fav-icon');
    if (favEl) favEl.setAttribute('href', prefs.favIcon || 'https://d2p078bqz5urf7.cloudfront.net/cloud/assets/img/logo/fav/ab-16x16.ico');

    const container = document.getElementById('root') || document.body;
    createRoot(container).render(<LoginApp deps={deps} />);
}

bootstrap();

// ── Utility functions (no jQuery) ─────────────────────────────────────────────

function getBrowserDetails() {
    try {
        const parser = new (window as any).UAParser();
        return {
            browser: parser.getBrowser().name,
            os: parser.getOS().name,
        };
    } catch {
        return {};
    }
}

function blockUnsupportedBrowser() {
    const json = getBrowserDetails();
    if ((json as any).browser !== 'Safari' || (json as any).os !== 'Windows') return;

    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop fade show';
    overlay.style.cssText = 'height:100%;width:100%;opacity:.8;';
    document.body.appendChild(overlay);

    const modalEl = document.createElement('div');
    modalEl.className = 'modal fade show';
    modalEl.setAttribute('tabindex', '-1');
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.style.cssText = 'padding-right:16px;display:block;';

    const dialog = document.createElement('div');
    dialog.className = 'modal-dialog modal-dialog-centered';
    dialog.style.top = '200px';

    const content = document.createElement('div');
    content.className = 'modal-content';
    content.style.borderRadius = '.7rem';

    const body = document.createElement('div');
    body.className = 'modal-body';

    const title = document.createElement('h5');
    title.className = 'modal-title';
    title.style.cssText = 'font-size:16px;margin-bottom:12px;';

    const icon = document.createElement('span');
    icon.className = 'mdi mdi-alert-triangle';
    icon.style.cssText = 'color:#fbbc05;padding-right:5px;';
    title.appendChild(icon);
    title.appendChild(document.createTextNode(' System Alert'));

    const msg = document.createElement('div');
    msg.style.cssText = 'font-size:13px;color:#777;';
    msg.textContent = 'We are no longer supporting Safari in Windows, Please switch to Chrome or any another browser to continue using EngageBay.';

    body.appendChild(title);
    body.appendChild(msg);
    content.appendChild(body);
    dialog.appendChild(content);
    modalEl.appendChild(dialog);
    document.body.appendChild(modalEl);
}

function loadGrowSumoScript() {
    const gs = document.createElement('script') as any;
    gs.src = 'https://snippet.growsumo.com/growsumo.min.js';
    gs.type = 'text/javascript';
    gs.async = 'true';
    gs.onload = gs.onreadystatechange = function () {
        const rs = this.readyState;
        if (rs && rs !== 'complete' && rs !== 'loaded') return;
        try { (window as any).growsumo._initialize('pk_ymL68yNmJctQwpYOvxvPV9NsxqQ8MJIQ'); } catch { }
    };
    const first = document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(gs, first);
}
